import { ApprovalFlows } from '@mint-sdk/api-approval-flows';
import { startOfDay } from 'date-fns';

import { PENDING_REQUEST_NOTIFICATION } from '../constants/singleFlow.js';
import { ApprovalTableItemData } from '../types/approversTable.js';
import { FlowFormOutput, FlowItem } from '../types/singleFlow.js';

const mapRequestGroupIntoList = (
  data: ApprovalFlows.FlowItemLite['requestGroup'],
): ApprovalTableItemData['requesters'] => {
  if (data.all) {
    // "All" is not a valid type of the requester group
    return [];
  }

  const isUserList = data.roles === null && data.users.length > 0;
  const isRoleList = data.users === null && data.roles.length > 0;

  if (isUserList) {
    return data.users.map((item) => {
      return {
        id: item.id,
        label: item.userName,
        type: 'user',
      };
    });
  }

  if (isRoleList) {
    return data.roles.map((item) => {
      return {
        id: item.id,
        label: item.role,
        type: 'role',
      };
    });
  }

  return [];
};

const mapApproveGroupIntoList = (
  data: ApprovalFlows.FlowItemLite['approval'],
): ApprovalTableItemData['approvers'] => {
  return data.map((item) => {
    const isUserEntity = item.type === 'users';
    const isRoleEntity = item.type === 'roles';

    if (isUserEntity) {
      const entity = item.users[0];
      return {
        id: entity.id,
        label: entity.userName,
        type: 'user',
      };
    }

    if (isRoleEntity) {
      const entity = item.role;
      return {
        id: entity.id,
        label: entity.role,
        type: 'role',
      };
    }

    return {
      id: item.operation,
      label: item.operation,
      type: 'operation',
    };
  });
};

export const mapFlowItemLiteIntoApproverTableItem = (
  data: ApprovalFlows.FlowItemLite,
): ApprovalTableItemData => {
  return {
    id: data.id,
    name: data.name,
    flowType: 'flight',
    status: data.archived ? 'inactive' : 'active',
    inUse: data.inUse,
    createdBy: 'NOT_PROVIDED', //TODO: ask BE to add the field
    createdDate: data.createdAt,
    lastEditedBy: 'NOT_PROVIDED', //TODO: ask BE to add the field
    lastEditedAt: data.updatedAt,
    requesters: mapRequestGroupIntoList(data.requestGroup),
    approvers: mapApproveGroupIntoList(data.approval),
    dateFrom: data.startDate ?? startOfDay(new Date()).toISOString(),
    dateTo: data.endDate,
  };
};

export const normalizeFlowItem = (data: ApprovalFlows.FlowItem): FlowItem => {
  return {
    id: data.id,
    name: data.name,
    flowType: 'flight',
    status: data.archived ? 'inactive' : 'active',
    inUse: data.inUse,
    createdBy: '',
    createdDate: data.createdAt,
    lastEditedBy: '',
    lastEditedAt: data.updatedAt,
    requesters: mapRequestGroupIntoList(data.requestGroup),
    approvers: mapApproveGroupIntoList(data.approval),
    dateFrom: data.startDate ?? startOfDay(new Date()).toISOString(),
    dateTo: data.endDate,
    pendingRequestNotification: {
      enabled: typeof data.notificationPeriod === 'number', //TODO: ask BE to provide real boolean field
      frequencyDays:
        data.notificationPeriod ??
        PENDING_REQUEST_NOTIFICATION.DEFAULT_FREQUENCY_DAYS,
    },
    approvedRequestNotification: {
      enabled: data.isEmailsForApprovedEntityEnabled,
      recipientEmails: data.emailsForApprovedEntity,
    },
  };
};

// CreateFlow transformers
export const mapRequestors = (
  requesterType: FlowFormOutput['requesterType'],
  requesterValue: FlowFormOutput['requesterValue'],
): ApprovalFlows.CreateFlowItemRequest['requestor'] => {
  return requesterType === 'user'
    ? {
        requestBy: 'users',
        requestorsData: requesterValue.map((item) => {
          return {
            userName: item.label,
            role: '',
            id: item.value,
          };
        }),
      }
    : {
        requestBy: 'roles',
        requestorsData: requesterValue.map((item) => {
          return {
            userName: null,
            role: item.label,
            id: item.value,
          };
        }),
      };
};

export const mapApprovers = (
  approvers: FlowFormOutput['approvers'],
): ApprovalFlows.CreateFlowItemRequest['approvers'] => {
  return approvers.reduce<ApprovalFlows.CreateFlowItemRequest['approvers']>(
    (acc, item, index) => {
      const nextOperation = approvers[index + 1]?.operation ?? null;

      if (item.type === 'user') {
        acc.push({
          approveBy: 'users',
          nextApproveCondition: nextOperation,
          approversData: [
            {
              id: item.entityId,
              userName: item.entityLabel,
              role: '',
            },
          ],
        });
        return acc;
      }

      acc.push({
        approveBy: 'roles',
        nextApproveCondition: nextOperation,
        approversData: [
          {
            id: item.entityId,
            userName: null,
            role: item.entityLabel,
          },
        ],
      });

      return acc;
    },
    [],
  );
};

export const mapEmails = (value: string): string[] => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return [];
  }

  return value.split(',').map((email) => email.trim());
};

export const mapFlowFormToCreateFlowItemRequest = (
  data: FlowFormOutput,
): ApprovalFlows.CreateFlowItemRequest => ({
  flowType: data.type,
  startDate: startOfDayUTC(data.dateFrom),
  endDate: data.dateTo ? endOfDayUTC(data.dateTo) : null,
  notifications: {
    enabled: data.pendingRequestNotification.enabled,
    notifyIn: data.pendingRequestNotification.frequencyDays,
  },
  flowName: data.name,
  requestor: mapRequestors(data.requesterType, data.requesterValue),
  approvers: mapApprovers(data.approvers),
  isEmailsForApprovedEntityEnabled: data.approvedRequestNotification.enabled,
  emailsForApprovedEntity: mapEmails(
    data.approvedRequestNotification.recipientEmails,
  ),
  notificationPeriod: data.pendingRequestNotification.enabled
    ? data.pendingRequestNotification.frequencyDays
    : null,
});

// UpdateFlowItem transformers
export const mergeApproversWithId = (
  approvers: FlowFormOutput['approvers'],
  ids: string[],
): ApprovalFlows.UpdateFlowItemRequest['approvers'] => {
  return approvers.reduce<ApprovalFlows.UpdateFlowItemRequest['approvers']>(
    (acc, item, index) => {
      const nextOperation = approvers[index + 1]?.operation ?? null;

      // Until inUse:true, approvers structure can be changed (new approvers can be added).
      // BE always expects "id" of the approver item, which does not exist yet for the new items.
      // Thus, new structure cannot be saved because of validation error.
      // TODO: resolve the collision - either prevent addition / removal of approvers for any existing flow OR update body type for the PUT endpoint
      const approverId = ids[index] ?? 'fallback';

      if (item.type === 'user') {
        acc.push({
          id: approverId,
          approveBy: 'users',
          nextApproveCondition: nextOperation,
          approversData: [
            {
              id: item.entityId,
              userName: item.entityLabel,
              role: '',
            },
          ],
        });
        return acc;
      }

      acc.push({
        id: approverId,
        approveBy: 'roles',
        nextApproveCondition: nextOperation,
        approversData: [
          {
            id: item.entityId,
            userName: null,
            role: item.entityLabel,
          },
        ],
      });

      return acc;
    },
    [],
  );
};

export const startOfDayUTC = (date: Date): string => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  ).toISOString();
};

export const endOfDayUTC = (date: Date): string => {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999,
    ),
  ).toISOString();
};

export const parseUTCDate = (dateString: string): Date => {
  const date = new Date(dateString);

  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};
